import { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import { LabelingProject, Project, ProjectId } from "../data/Project";
import { useFirebase } from "./FirebaseProvider";
import { doc, getDoc, setDoc, onSnapshot, collection, query, where } from "firebase/firestore";

export type ProjectContextType = {
    /**
     * Gets the labeling project with the given ID.
     * @param projectId The ID of the project.
     * @returns The project with the given ID.
     */
    getLabelingProject: (projectId: ProjectId) => Promise<LabelingProject>;

    /**
     * Returns true if the project with the given ID exists.
     * @param projectId The ID of the project.
     * @returns true if the project with the given ID exists, false otherwise.
     */
    projectExists: (projectId: ProjectId) => Promise<boolean>;

    /**
     * Creates a new project.
     * @param project The project to create.
     * @returns The ID of the created project.
     */
    createProject: (project: Project) => Promise<ProjectId>;

    /**
     * Updates the project.
     * @param project The project to update.
     */
    updateProject: (project: Project) => Promise<void>;

    /**
     * The list of projects.
     */
    projects: Project[];
}

export const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider = ({ children }: { children: React.ReactNode }) => {
    const { db, user } = useFirebase();
    
    const [projects, setProjects] = useState<Project[]>([]);

    useEffect(() => {
        if(!user) {
            return;
        }

        const q = query(collection(db, 'projects'), where("allowedUsers", "array-contains", user?.uid ?? ''));

        onSnapshot(q, (projectsCollection) => {
            const projects = projectsCollection.docs.map(doc => doc.data() as Project);
            
            setProjects(projects);
        });
    }, [db, setProjects, user]);
    
    const getLabelingProject = async (projectId: ProjectId) => {
        const docRef = doc(db, 'projects', projectId);

        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return docSnap.data() as LabelingProject;
        } else {
            throw new Error(`Project with ID ${projectId} not found.`);
        }
    };

    const projectExists = async (projectId: ProjectId) => {
        const docRef = doc(db, 'projects', projectId);

        const docSnap = await getDoc(docRef);

        return docSnap.exists();
    };

    const createProject = async (project: Project) => {
        const docRef = doc(db, 'projects', project.id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            throw new Error('Document already exists.');
        }

        await setDoc(docRef, project);

        return project.id;
    };

    const updateProject = async (project: Project) => {
        const docRef = doc(db, 'projects', project.id);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
            throw new Error('Document does not exist.');
        }

        await setDoc(docRef, project);
    }

    const context = { createProject, getLabelingProject, projectExists, updateProject, projects };

    return (
        <ProjectContext.Provider value={context}>
            {children}
        </ProjectContext.Provider>
    )
}

export const useProject = () => {
    const context = useContext(ProjectContext);

    if (!context) {
        throw new Error('useProject must be used within a ProjectProvider');
    }

    return context;
}